import React, { useState, useEffect } from "react"
import * as uuid from "uuid"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as layouts from "layouts"
import * as sessions from "sessions"
import * as brands from "brands"
import * as api from "brandguard/api"
import * as bgvideos from "brandguard/display/brandguard/videos"
import * as bglayouts from "brandguard/components/layouts"
import * as bgapprovals from "brandguard/display/approval"
import * as authz from "authz"
import { useSettings } from "accounts/settings"
import { State } from "accounts/api"
import classnames from "classnames"
import * as scores from "brandguard/components/layouts/scores"
import * as debugx from "x/debugx"
import * as icons from "icons"
import JSZip from "jszip"
import * as media from "media"
import * as bgscores from "brandguard/display/scores"
import * as typography from "typography"
import * as timex from "timex"
import { FeedbackPanel, FeedbackHistory } from "brandguard/feedback"

export default function Display(): JSX.Element {
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const permission = authzaccount.current
	const settings = useSettings()
	const bearertoken = sessions.useToken()
	const account = sessions.useAccount()
	const brand = brands.caching.useCached()
	const mtoggle = layouts.modals.useToggle()

	const [loading, setLoading] = useState(true)
	const [selectedItem, setSelectedItem] = useState(undefined as api.VideoSearchResponseItem | undefined)
	const [checkedItems, setCheckedItems] = useState([] as api.VideoSearchResponseItem[])
	const [feedbackOpen, setFeedbackOpen] = useState(false)
	const [filterFocused, setFilterFocused] = useState(false)

	const [undoItems, setUndoItems] = useState<api.VideoSearchResponseItem[]>([])

	const _searchReq = api.videos.uploads.searches.zero({
		offset: 0n,
		limit: 20n,
		brand_id: brand.id,
		queues: [brand.id],
		observation: [api.Prediction.INREVIEW],
	})

	const [searchReq, setSearchReq] = useState(_searchReq)
	const [cursorKey, setCursorKey] = useState(uuid.v4())

	const [videos, setVideos] = useState({
		items: [],
		next: searchReq,
	} as api.VideoSearchResponse)

	useEffect(() => {
		if (searchReq.brand_id === uuid.NIL) return
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.videos.reviewable.search(searchReq.brand_id, searchReq, bearertoken))
			.then(setVideos)
			.catch(httpx.errors.cancellation(() => undefined))
			.catch((cause) => console.error("unable to retry brandguard approval video loading", cause))
			.finally(() => {
				setLoading(false)
			})
		return p.cancel
	}, [searchReq])

	useEffect(() => {
		if (!selectedItem?.event) return
		const event = selectedItem.event
		mtoggle(
			<layouts.Flex styled flexDirection="column">
				<bgapprovals.VideoPreview
					item={selectedItem}
					flex="1"
					onClose={() => {
						mtoggle(undefined)
						setSelectedItem(undefined)
					}}
				>
					<typography.h3 fontWeight="700" fontSize="18px" lineHeight="30px" color="#4D5E80">
						Asset Scores
					</typography.h3>
					<bgvideos.Scores item={selectedItem} />
					<bgapprovals.status.default>
						<bgapprovals.status.Review
							item={selectedItem}
							previtemstate={undoItems.find((u) => u.media?.id === selectedItem.media?.id)}
							agree={approve}
							disagree={reject}
							undo={undo}
							borderRadius="10px"
						/>
					</bgapprovals.status.default>
					<layouts.containers.flex py="10px">
						<FeedbackPanel eventType="video" eventIds={[event.id]} />
					</layouts.containers.flex>
					<layouts.containers.flex py="10px">
						<FeedbackHistory eventType="video" eventId={event.id} />
					</layouts.containers.flex>
					<authz.Protected enabled={debugx.alpha.enabled()}>
						<bgscores.layouts.accordion.Container title="Debug Info" textAlign="initial">
							<layouts.containers.flex flexDirection="column" fontSize="12px">
								<layouts.containers.flex>ID: {selectedItem.event?.id}</layouts.containers.flex>
								<layouts.containers.flex>Confidence: {selectedItem.event?.confidence}</layouts.containers.flex>
								<layouts.containers.flex>Brand Voice: {selectedItem.event?.video_brand_voice}</layouts.containers.flex>
								<layouts.containers.flex>Violence: {selectedItem.event?.video_violence}</layouts.containers.flex>
								<layouts.containers.flex>Nudity: {selectedItem.event?.video_sexually_explicit}</layouts.containers.flex>
							</layouts.containers.flex>
						</bgscores.layouts.accordion.Container>
					</authz.Protected>
				</bgapprovals.VideoPreview>
			</layouts.Flex>,
		).then(() => setSelectedItem(undefined))
	}, [selectedItem])

	const onVideoUpdate = (item: api.VideoSearchResponseItem): void => {
		const remove = (items: api.VideoSearchResponseItem[]) => items.filter((i) => i.media?.id !== item.media?.id)
		const replace = (items: api.VideoSearchResponseItem[]) =>
			items.map((i) => (i.media?.id === item.media?.id ? item : i))
		const removed = timex.from.iso(item.media?.tombstoned_at || timex.infinity().toISO()) < timex.local()
		const op = removed ? remove : replace
		setVideos((prevState) => ({ ...prevState, items: op(prevState.items) }))
	}

	const reject = (
		item: api.VideoSearchResponseItem & { event: api.VideoEvent },
	): Promise<api.VideoSearchResponseItem> => {
		return api.videos.reviewable
			.publish(
				item.event.id,
				item.event.brand_id,
				{
					...item,
					event: {
						...item.event,
						observation: api.Prediction.REJECTED,
					},
				},
				authzc.bearer(authzaccount),
			)
			.then((res_i) => {
				setUndoItems([...undoItems, ...[item]])
				setVideos((prevState) => ({
					...prevState,
					items: prevState.items.map((i) => (i.media?.id === res_i.media?.id ? res_i : i)),
				}))
				if (selectedItem && selectedItem.media?.id === res_i.media?.id) setSelectedItem(res_i)
				return res_i
			})
	}

	const approve = (
		item: api.VideoSearchResponseItem & { event: api.VideoEvent },
	): Promise<api.VideoSearchResponseItem> => {
		return api.videos.reviewable
			.publish(
				item.event.id,
				item.event.brand_id,
				{
					...item,
					event: {
						...item.event,
						observation: api.Prediction.APPROVED,
					},
				},
				authzc.bearer(authzaccount),
			)
			.then((res_i) => {
				setUndoItems([...undoItems, ...[item]])
				setVideos((prevState) => ({
					...prevState,
					items: prevState.items.map((i) => (i.media?.id === res_i.media?.id ? res_i : i)),
				}))
				if (selectedItem && selectedItem.media?.id === res_i.media?.id) setSelectedItem(res_i)
				return res_i
			})
	}

	const undo = (
		_current: api.VideoSearchResponseItem & { event: api.VideoEvent },
		previously: api.VideoSearchResponseItem & { event: api.VideoEvent },
	): Promise<api.VideoPatchResponse> => {
		return api.videos.uploads
			.patch(previously.event.brand_id, previously.event.id, previously, authzc.bearer(authzaccount))
			.then((resp_i) => {
				setUndoItems(undoItems.filter((i) => i.media?.id !== previously.media?.id))
				setVideos((prevState) => ({
					...prevState,
					items: prevState.items.map((i) => (i.media?.id === previously.media?.id ? previously : i)),
				}))
				if (selectedItem && selectedItem.media?.id === previously.media?.id) setSelectedItem(previously)
				return resp_i
			})
	}

	const _allselected = videos.items.length > 0 && checkedItems.length === videos.items.length

	const handleSelectAll = (): void => {
		if (_allselected) {
			setCheckedItems([])
			return
		}
		setCheckedItems(videos.items)
	}

	const appchecked = searchReq.approved.length === 1 && searchReq.approved[0] === api.Prediction.APPROVED
	const rejchecked = searchReq.approved.length === 1 && searchReq.approved[0] === api.Prediction.REJECTED
	const fcnt = bgapprovals.CalculateFilterSCount(_searchReq, searchReq)

	return (
		<layouts.containers.flex flexDirection="column-reverse" overflow="hidden">
			<layouts.pagination.Cursor
				key={cursorKey}
				my="auto"
				justifyContent="center"
				current={searchReq.offset}
				advance={Number(videos.next!.offset) === -1 ? undefined : videos.next?.offset}
				onChange={(next) => {
					setSearchReq({
						...searchReq,
						offset: next,
					})
				}}
			/>
			<layouts.loading.screen
				className="brandguard-working-area"
				loading={loading}
				flex="1"
				overflow="hidden"
				icon={<></>}
				flexDirection="row"
			>
				<layouts.containers.flex className="center-panel" flex="6" flexDirection="column">
					<layouts.containers.flex flexDirection="column" flex="1" minHeight="100px">
						<bglayouts.OverlaysContainer overflow="auto" background={layouts.theme.backgrounds.whitealpha80}>
							<layouts.containers.ResponsiveGrid gap={20} p="25px">
								{videos.items.map((item) => {
									return (
										<bgapprovals.videos.CardDisplay
											key={item.media?.id}
											item={item}
											active={selectedItem && item?.media?.id === selectedItem.media?.id}
											onCardClick={() => {
												setSelectedItem(item)
											}}
											onChange={onVideoUpdate}
											onChecked={(checked) =>
												setCheckedItems((prevState) =>
													checked ? [...prevState, item] : prevState.filter((i) => i.media?.id !== item.media?.id),
												)
											}
											score={
												<authz.Protected enabled={settings.confidence !== State.DISABLED}>
													<layouts.containers.absolute>
														<scores.BGScoreVideoSimple event={item.event!} />
													</layouts.containers.absolute>
												</authz.Protected>
											}
											panel={
												<bgapprovals.status.default>
													<bgapprovals.status.Review
														item={item}
														previtemstate={undoItems.find((u) => u.media?.id === item.media?.id)}
														agree={approve}
														disagree={reject}
														undo={undo}
														borderRadius="0 0 10px 10px"
													/>
												</bgapprovals.status.default>
											}
											find={api.videos.uploads.find}
											checked={checkedItems.some((i) => i.media?.id === item.media?.id)}
										/>
									)
								})}
							</layouts.containers.ResponsiveGrid>
							<layouts.loading.pending loading={loading || videos.next?.offset === searchReq.offset}>
								{videos.items.length === 0 && <bgapprovals.layouts.ZeroState />}
							</layouts.loading.pending>
						</bglayouts.OverlaysContainer>
					</layouts.containers.flex>
				</layouts.containers.flex>
			</layouts.loading.screen>
			{feedbackOpen && checkedItems.length > 0 && (
				<layouts.containers.flex mb="10px">
					<FeedbackPanel eventType="video" eventIds={checkedItems.filter((i) => i.event).map((i) => i.event!.id)} />
				</layouts.containers.flex>
			)}
			<layouts.containers.flex flexDirection="row" className={classnames(bgapprovals.layouts.styledactions)}>
				<layouts.containers.flex flexDirection="column" mb="5px" zIndex="0" position="relative">
					<bgapprovals.layouts.FilterButton
						width="145px"
						height="40px"
						borderRadius="4px"
						onClick={() => setFilterFocused(!filterFocused)}
						className={fcnt > 0 ? "active" : ""}
					>
						<layouts.containers.flex flexDirection="row" justifyContent="center">
							<layouts.containers.box>
								<icons.bgapproval.filter />
							</layouts.containers.box>
							<layouts.containers.box px="10px">Filter{fcnt > 0 ? `/${fcnt}` : ""}</layouts.containers.box>
							<layouts.containers.box>
								{filterFocused ? <icons.bgapproval.arrowDown /> : <icons.bgapproval.arrowDown />}
							</layouts.containers.box>
						</layouts.containers.flex>
					</bgapprovals.layouts.FilterButton>
					<layouts.containers.flex display={filterFocused ? "flex" : "none"} position="absolute" top="100%" pt="5px">
						<bgapprovals.FilterDisplay
							onFilterClear={() => {
								setSearchReq(_searchReq)
								setCursorKey(uuid.v4())
							}}
							_searchReq={_searchReq}
							onFilterChange={(r) => {
								setSearchReq({ ...searchReq, ...r, ...{ offset: 0n } })
								setCursorKey(uuid.v4())
							}}
						>
							<bgapprovals.Checkbox
								description="Assets that were approved"
								checked={appchecked}
								onChange={() => {
									setSearchReq((prevState) => ({
										...prevState,
										approved: appchecked ? _searchReq.approved : [api.Prediction.APPROVED],
										offset: 0n,
									}))
									setCursorKey(uuid.v4())
								}}
							/>
							<bgapprovals.Checkbox
								description="Assets that were rejected"
								checked={rejchecked}
								onChange={() => {
									setSearchReq((prevState) => ({
										...prevState,
										approved: rejchecked ? _searchReq.approved : [api.Prediction.REJECTED],
										offset: 0n,
									}))
									setCursorKey(uuid.v4())
								}}
							/>
						</bgapprovals.FilterDisplay>
					</layouts.containers.flex>
				</layouts.containers.flex>
				<layouts.containers.flex className="actions" justifyContent="flex-end" flex="10">
					{permission.brandguard_review && <bgapprovals.layouts.FeedbackToggle
						disabled={checkedItems.length === 0}
						onClick={() => setFeedbackOpen((v) => !v)}
					>
						{feedbackOpen ? "Hide Feedback" : "Give Feedback"}
					</bgapprovals.layouts.FeedbackToggle>}
					<bgapprovals.layouts.SelectAll
						disabled={videos.items.length === 0}
						cursor="pointer"
						className="select-all-action"
						onClick={handleSelectAll}
					>
						{_allselected ? "Unselect All" : "Select All"}
					</bgapprovals.layouts.SelectAll>
					<authz.Protected enabled={permission.brandguard_upload && account.correction_enabled}>
						<bgvideos.GenerateCorrection
							items={checkedItems}
							onCorrectionComplete={() => {
								setCheckedItems([])
								setSearchReq((prev) => ({ ...prev }))
							}}
						/>
					</authz.Protected>
					<authz.Protected
						enabled={permission.brandguard_upload && checkedItems.length > 0}
						rejected={
							<layouts.containers.flex className="download-action">
								<icons.brandguard.download />
							</layouts.containers.flex>
						}
					>
						<layouts.containers.flex
							className="download-action"
							cursor="pointer"
							onClick={() => {
								const zip = new JSZip()
								const promises: Promise<api.VideoPatchRequest | JSZip>[] = []
								checkedItems.forEach((i) => {
									promises.push(
										media.cache.current(authzaccount.current.id, i.media!.md5, bearertoken).then((url) => {
											return fetch(url)
												.then((response) => {
													return response.arrayBuffer()
												})
												.then((videoBuffer) => {
													const videoName = media.downloads.filename(i.media!.md5, i.media!.mimetype)
													return zip.file(videoName, videoBuffer)
												})
										}),
									)
								})
								Promise.all(promises).then(() => {
									zip.generateAsync({ type: "blob" }).then((content) => {
										const downloadLink = document.createElement("a")
										downloadLink.href = URL.createObjectURL(content)
										downloadLink.download = "brandguard-videos.zip"
										downloadLink.click()
									})
								})
							}}
						>
							<icons.brandguard.download />
						</layouts.containers.flex>
					</authz.Protected>
				</layouts.containers.flex>
			</layouts.containers.flex>
		</layouts.containers.flex>
	)
}
